import { useState, useEffect } from 'react';
import { Plus, Send, Trash2, Users, User, Calendar, Loader2, MessageCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DailyMessage {
  id: string;
  title: string;
  message: string | null;
  category: string;
  xp_reward: number;
  target_type: string;
  target_worker_id: string | null;
  status: string;
  created_at: string;
  delivered_at: string | null;
  target_worker?: { display_name: string } | null;
}

interface Worker {
  id: string;
  display_name: string;
}

interface DailyMessageConfigProps {
  companyId: string;
  siteId?: string;
}

const CATEGORIES = [
  { value: 'motivation', label: 'Motivasi', color: 'bg-green-500/10 text-green-500' },
  { value: 'safety_tip', label: 'Tips Keselamatan', color: 'bg-blue-500/10 text-blue-500' },
  { value: 'reminder', label: 'Pengingat', color: 'bg-amber-500/10 text-amber-500' },
  { value: 'recognition', label: 'Apresiasi', color: 'bg-pink-500/10 text-pink-500' },
  { value: 'announcement', label: 'Pengumuman', color: 'bg-purple-500/10 text-purple-500' },
];

export default function DailyMessageConfig({ companyId, siteId }: DailyMessageConfigProps) {
  const [messages, setMessages] = useState<DailyMessage[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('motivation');
  const [targetType, setTargetType] = useState<'all' | 'individual'>('all');
  const [targetWorkerId, setTargetWorkerId] = useState<string>('');
  const [xpReward, setXpReward] = useState(5);

  useEffect(() => {
    loadData();
  }, [companyId, siteId]);

  const loadData = async () => {
    try {
      // Use any to avoid deep type instantiation issues with Supabase
      const client = supabase as any;
      
      // Load existing daily messages
      const { data: messagesData, error } = await client
        .from('interventions')
        .select('id, title, message, category, xp_reward, target_type, target_worker_id, status, created_at, delivered_at')
        .eq('company_id', companyId)
        .eq('delivery_method', 'daily_message')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Load worker names for the messages
      const messagesWithWorkers: DailyMessage[] = [];
      for (const msg of (messagesData || [])) {
        let workerName: string | null = null;
        if (msg.target_worker_id) {
          const { data: workerData } = await client
            .from('worker_profiles')
            .select('display_name')
            .eq('id', msg.target_worker_id)
            .single();
          workerName = workerData?.display_name || null;
        }
        messagesWithWorkers.push({
          ...msg,
          target_worker: workerName ? { display_name: workerName } : null
        });
      }
      setMessages(messagesWithWorkers);

      // Load workers for targeting
      const { data: workersData } = await client
        .from('worker_profiles')
        .select('id, display_name')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (workersData) {
        setWorkers(workersData.map((w: any) => ({ id: w.id, display_name: w.display_name })));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load daily messages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    setCreating(true);
    try {
      if (targetType === 'all') {
        // Create one message per worker
        const workerIds = workers.map(w => w.id);
        const insertData = workerIds.map(workerId => ({
          company_id: companyId,
          site_id: siteId || null,
          delivery_method: 'daily_message',
          category,
          target_type: 'individual',
          target_worker_id: workerId,
          trigger_type: 'manual',
          title,
          message,
          xp_reward: xpReward,
          status: 'pending',
        }));

        const { error } = await supabase.from('interventions').insert(insertData);
        if (error) throw error;
        toast.success(`Daily message sent to ${workerIds.length} workers`);
      } else {
        // Create for single worker
        const { error } = await supabase.from('interventions').insert({
          company_id: companyId,
          site_id: siteId || null,
          delivery_method: 'daily_message',
          category,
          target_type: 'individual',
          target_worker_id: targetWorkerId,
          trigger_type: 'manual',
          title,
          message,
          xp_reward: xpReward,
          status: 'pending',
        });
        if (error) throw error;
        toast.success('Daily message created');
      }

      // Reset form
      setTitle('');
      setMessage('');
      setCategory('motivation');
      setTargetType('all');
      setTargetWorkerId('');
      setXpReward(5);
      setShowCreateDialog(false);
      loadData();
    } catch (error) {
      console.error('Error creating message:', error);
      toast.error('Failed to create daily message');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('interventions')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast.success('Message deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete message');
    }
  };

  const getCategoryInfo = (cat: string) => {
    return CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Daily Messages</h2>
          <p className="text-sm text-muted-foreground">
            Send personalized messages that appear on worker's home screen
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Message
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Daily Message</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="E.g., Selamat pagi! Ingat keselamatan..."
                />
              </div>

              <div className="space-y-2">
                <Label>Message (Optional)</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Additional message content..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Send To</Label>
                <Select value={targetType} onValueChange={(v) => setTargetType(v as 'all' | 'individual')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workers ({workers.length})</SelectItem>
                    <SelectItem value="individual">Specific Worker</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {targetType === 'individual' && (
                <div className="space-y-2">
                  <Label>Select Worker</Label>
                  <Select value={targetWorkerId} onValueChange={setTargetWorkerId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a worker..." />
                    </SelectTrigger>
                    <SelectContent>
                      {workers.map((worker) => (
                        <SelectItem key={worker.id} value={worker.id}>
                          {worker.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>XP Reward</Label>
                <Input
                  type="number"
                  value={xpReward}
                  onChange={(e) => setXpReward(parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleCreate}
                  disabled={creating || !title.trim()}
                  className="flex-1"
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Message
                </Button>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Total Sent</p>
          <p className="text-2xl font-bold">{messages.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Pending</p>
          <p className="text-2xl font-bold text-amber-500">
            {messages.filter(m => m.status === 'pending').length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Viewed</p>
          <p className="text-2xl font-bold text-green-500">
            {messages.filter(m => m.status === 'completed').length}
          </p>
        </Card>
      </div>

      {/* Messages List */}
      <div className="space-y-3">
        {messages.length === 0 ? (
          <Card className="p-8 text-center">
            <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No daily messages yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create one to send personalized messages to workers
            </p>
          </Card>
        ) : (
          messages.map((msg) => (
            <Card key={msg.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={getCategoryInfo(msg.category).color}>
                      {getCategoryInfo(msg.category).label}
                    </Badge>
                    <Badge variant="outline" className={
                      msg.status === 'completed' ? 'bg-green-500/10 text-green-500' :
                      msg.status === 'pending' ? 'bg-amber-500/10 text-amber-500' :
                      'bg-muted text-muted-foreground'
                    }>
                      {msg.status}
                    </Badge>
                  </div>
                  <h3 className="font-semibold truncate">{msg.title}</h3>
                  {msg.message && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                      {msg.message}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {msg.target_worker?.display_name || 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(msg.created_at).toLocaleDateString('id-ID')}
                    </span>
                    {msg.xp_reward > 0 && (
                      <span>+{msg.xp_reward} XP</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(msg.id)}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}